import { useCallback } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  AppMultiSelect,
  AppRemixIcon,
  AppNotificationAlert,
} from "@/layout/AppComponents";
import axios from "axios";
import { Formik } from "formik";
import ReactDOMServer from "react-dom/server";
import { axiosPath } from "../../../../../../routing/helpers/router";
import Translator from "../../../../../components/helpers/Translator";
import colors from "../../../../../../constants/colors";
import errorHandler from "@/layout/components/error/ErrorHandler";
import * as currentPageAction from "../../../../../../store/actions/currentPageAction";
import _ from "lodash";

export default function DelegatedResponsibleTab(props) {
  const { taskID } = props;
  const { moduleBridgeHex, view } = useParams();
  const dispatch = useDispatch();
  const moduleData = useSelector((s) => s.module[moduleBridgeHex].moduleData);
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const levels = useSelector((state) => state.currentPage?.levels);
  const level2 = useSelector((state) => state.currentPage?.levels?.level2);

  const handleSave = useCallback(
    async (payload, { resetForm }) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_correctiveActions_task_responsible_delegate"
          ),
          {
            moduleBridgeHex,
            taskID,
            view,
            ...payload,
          }
        )
        .then((r) => {
          dispatch(
            currentPageAction.change("levels", {
              ...levels,
              level2: {
                ...level2,
                [taskID]: {
                  ...level2?.[taskID],
                  responsibles: r.data?.responsibles,
                },
              },
            })
          );
          resetForm();
          AppNotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="module-correctiveActions" name="completed" />
            ),
            message: ReactDOMServer.renderToString(
              <Translator
                file="module-correctiveActions"
                name="responsibles.delegation.saved"
              />
            ),
            type: "success",
            duration: 3000,
            position: "top-right",
          });
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );

  return (
    <Container>
      {level2?.[taskID]?.responsibles.length === 0 ? (
        <div className="alert alert-info">
          <Translator
            file="module-correctiveActions"
            name="module.ca.no-employees"
          />
        </div>
      ) : (
        <Formik
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={{
            subResponsibles: [],
            deletedSubResponsibles: [],
          }}
          onSubmit={handleSave}
        >
          {({ setFieldValue, handleSubmit, values, isSubmitting, errors }) => (
            <form onSubmit={handleSubmit}>
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-6">
                    <h4>
                      <b>
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.employee-s"
                        />
                      </b>
                    </h4>
                    <ul>
                      {level2?.[taskID]?.responsibles.map((r) => (
                        <li key={r?.id}>
                          <h3>
                            <span>{r?.name} </span>
                          </h3>
                          <ul>
                            {r?.responsibles?.map((subRes) => (
                              <li key={subRes?.id}>
                                <Responsible
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-correctiveActions"
                                      name={
                                        values.deletedSubResponsibles.includes(
                                          subRes.id
                                        )
                                          ? "restore.delegate.responsible"
                                          : "delete.delegate.responsible"
                                      }
                                    />
                                  )}
                                  className="mb-2 pointer"
                                  $isDeleted={values?.deletedSubResponsibles.includes(
                                    subRes.id
                                  )}
                                  onClick={() => {
                                    if (
                                      values.deletedSubResponsibles.includes(
                                        subRes.id
                                      )
                                    ) {
                                      setFieldValue(
                                        "deletedSubResponsibles",
                                        values.deletedSubResponsibles.filter(
                                          (id) => id !== subRes.id
                                        )
                                      );
                                    } else {
                                      setFieldValue("deletedSubResponsibles", [
                                        ...values.deletedSubResponsibles,
                                        subRes.id,
                                      ]);
                                    }
                                  }}
                                >
                                  <span>{subRes?.name} </span>
                                  <AppRemixIcon
                                    icon="delete-bin-6"
                                    className="ml-2"
                                  />
                                </Responsible>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {_.map(level2?.[taskID]?.responsibles, "id").includes(
                    userBridge?.userID
                  ) && (
                    <div className="col-lg-6">
                      <AppMultiSelect
                        icon={<AppRemixIcon icon="group" />}
                        options={moduleData?.responsiblesAndIssuers || []}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-correctiveActions"
                            name="delegate.responsibles"
                          />
                        )}
                        value={values?.subResponsibles}
                        error={errors?.subResponsibles}
                        onChange={(v) => setFieldValue("subResponsibles", v)}
                        hasSearhBox
                        isClearable
                      />
                    </div>
                  )}
                </div>
                {_.map(level2?.[taskID]?.responsibles, "id").includes(
                  userBridge?.userID
                ) && (
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="float-right">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn btn-success btn-sm"
                        >
                          <Translator file="general" name="general.edit" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </Formik>
      )}
    </Container>
  );
}

const Container = styled.div`
  height: 400px;
  overflow: auto;
`;

const Responsible = styled.div`
  display: inline-flex;
  align-content: center;

  span {
    text-decoration: ${(props) => (props.$isDeleted ? "line-through" : "")};
  }
  i {
    color: ${(props) => (props.$isDeleted ? colors.grey400 : colors.danger)};
  }
`;
